// The site's design photos: hero, footer, header palm, mood tiles and the
// sample cards shown while the catalogs are empty.
//
// All are generated by Sanity AI (no licence needed, as dubai's design
// photos), in throwaway drafts of the `dubai` dataset — Miami has no Sanity
// project — then cut and saved as webp with `sharp`: the mood tiles and sample
// cards on 2026-11-01 (640 × 480 and 960 × 640, 2× their size on the page),
// the same subjects as the mockup's, whose 1024 px cuts were too soft. A new
// photo takes a new file name: /images/* is cached for a week.

import type { MoodTile, SampleCard } from '@todo/web-ui/lib/site-ui';

/**
 * Hero photo: Biscayne Bay and the downtown skyline — not cut from the mockup
 * but generated anew by Sanity AI (2026-10-31, 1600 × 656), the mockup's cut
 * being too soft. Webp q72 from the Sanity image CDN (`?fm=webp&q=72`).
 */
export const HERO_IMAGE = '/images/hero-biscayne.webp';

/** The hero's intrinsic size. */
export const HERO_SIZE = { width: 1600, height: 656 } as const;

/** Smaller copies (webp, 1200 q72 and 750 q70). Regenerate them when the photo changes. */
export const HERO_SRCSET = [
  '/images/hero-biscayne-750.webp 750w',
  '/images/hero-biscayne-1200.webp 1200w',
  `${HERO_IMAGE} ${HERO_SIZE.width}w`,
].join(', ');

/**
 * Footer background: Biscayne Bay and the downtown skyline, palms on the right
 * — the mockup's newsletter band, generated anew by Sanity AI (2026-10-31,
 * 1792 × 592, webp q74), the band's photo having its text and form baked in.
 */
export const FOOTER_IMAGE = '/images/footer-biscayne.webp';

/**
 * The palm frond hanging into the header's top-left corner, as in the mockup:
 * generated by Sanity AI on white (2026-10-31), its tip cut out, the white
 * turned transparent and recoloured to the hero palm's greens (each pixel's
 * brightness mapped onto the photo palm's colour spread), so it reads as the
 * same kind of tree, not lime (webp, 2× the 140 px desktop width).
 */
export const HEADER_CORNER = { image: '/images/header-palm.webp', width: 280, height: 399 } as const;

/** "Curated for Your Mood", in the mockup's order: four moods and the family category (the mockup's events tile is left out — no /events yet). */
export const MOOD_TILES: readonly MoodTile[] = [
  { key: 'collections.date-night.title', path: 'date-night', image: '/images/mood-date-night-cruise.webp' },
  { key: 'collections.beach-days.title', path: 'beach-days', image: '/images/mood-beach-tower.webp' },
  { key: 'collections.luxury.title', path: 'luxury', image: '/images/mood-luxury-yacht.webp' },
  { key: 'collections.art-and-culture.title', path: 'art-and-culture', image: '/images/mood-art-wynwood.webp' },
  { key: 'mood.family', path: 'family', image: '/images/mood-family-flamingos.webp' },
];

/** The three "Featured This Week" cards of the mockup (copy under `sample.<key>`). */
export const SAMPLE_CARDS: readonly SampleCard[] = [
  { key: 'dinner', path: 'date-night', image: '/images/featured-dinner-terrace.webp' },
  { key: 'beach', path: 'beach-days', image: '/images/featured-beach-tower.webp' },
  { key: 'yacht', path: 'luxury', image: '/images/featured-yacht-skyline.webp' },
];
